import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { ISignalProviderListSummary } from '@brushfire/types'
import type { SignalProviderVerifiedFilter } from '../lib/signal-provider-list-filters'
import { applyVerifiedQuickStatFilter } from '../lib/signal-provider-list-filters'
import {
  SP_LIST_PILL_GAP_PX,
  SP_LIST_PILLS_TO_TABLE_GAP_PX,
} from '../lib/signal-provider-list-layout'

const PILL_STYLES = {
  verified: { bg: '#d1fae5', border: '#10b981', color: '#065f46' },
  unverified: { bg: '#fef3c7', border: '#f59e0b', color: '#92400e' },
  failed: { bg: '#fee2e2', border: '#ef4444', color: '#991b1b' },
} as const

interface Props {
  summary: ISignalProviderListSummary
  verifiedFilter: SignalProviderVerifiedFilter
  onVerifiedFilterChange: (next: SignalProviderVerifiedFilter) => void
}

export function SignalProviderQuickStatPills({
  summary,
  verifiedFilter,
  onVerifiedFilterChange,
}: Props) {
  const pills = [
    { key: 'verified' as const, label: 'Verified', count: summary.verified },
    { key: 'unverified' as const, label: 'Unverified', count: summary.unverified },
    { key: 'failed' as const, label: 'Failed', count: summary.failed },
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${SP_LIST_PILL_GAP_PX}px`,
        mb: `${SP_LIST_PILLS_TO_TABLE_GAP_PX}px`,
      }}
      data-testid="signal-provider-quick-stat-pills"
    >
      {pills.map((pill) => {
        const style = PILL_STYLES[pill.key]
        const isActive = verifiedFilter === pill.key
        return (
          <Box
            key={pill.key}
            component="button"
            type="button"
            data-testid={`signal-provider-quick-stat-${pill.key}`}
            onClick={() =>
              onVerifiedFilterChange(applyVerifiedQuickStatFilter(verifiedFilter, pill.key))
            }
            sx={{
              border: `1px solid ${style.border}`,
              bgcolor: style.bg,
              color: style.color,
              borderRadius: '100px',
              px: 1.25,
              py: 0.625,
              cursor: 'pointer',
              fontFamily: '"Inter", sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              boxShadow: isActive ? `inset 0 0 0 1px ${style.border}` : 'none',
            }}
          >
            <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
              {pill.label}: {pill.count.toLocaleString()}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}
